// @flow
import type {Validator} from 'redux-form-validators';
import type {ErrorsMap, ValidatorsMap} from './validators.types';

/**
 * Map validators functions to redux-form rules
 * @param rules
 * @param values
 * @returns {{}}
 */

export const mapValidatorsToRules = <T>(rules: ValidatorsMap<T>, values: any): ErrorsMap => {
  const map = {};

  if (!rules || !values) {
    return {};
  }

  Object.keys(rules).map(key => {
    const currentRule = rules[key];
    const currentValue = values[key];

    if (typeof currentRule === 'function') {
      map[key] = currentRule(currentValue, values);
    } else if (Array.isArray(currentRule)) {
      map[key] = currentRule.map(validatorFunc => validatorFunc(currentValue, values)).find(x => x);
    } else if (typeof currentRule === 'object' && currentRule !== null) {
      map[key] = mapValidatorsToRules(currentRule, currentValue);
    }
  });

  return map;
};

/**
 * Map validations errors & scroll to first
 * @return void
 */
export const scrollToErrors = (): void => {
  setTimeout(() => {
    // @todo added focus
    const validationElements = [
      ...document.getElementsByClassName('ui-form__validation'),
      ...document.getElementsByClassName('xsui-messages__validation'),
      ...document.getElementsByClassName('xsui-notification--status-error')
    ];

    validationElements.sort(
      (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top
    );

    validationElements.forEach(element => {
      const xsuiCollapse = element.closest('.xsui-collapse');
      if (!xsuiCollapse) {
        return;
      }
      const collapseWrapper =
        xsuiCollapse && xsuiCollapse.getElementsByClassName('ReactCollapse--collapse')[0];

      if (!collapseWrapper) {
        return;
      }

      if (collapseWrapper.style.height === '0px') {
        collapseWrapper.style.height = 'auto';
        xsuiCollapse.getElementsByTagName('button')[0].click();
      }
    });

    if (validationElements.length) {
      const parentElement =
        validationElements[0].closest('.xsui-form-group') || validationElements[0].parentElement;
      if (!parentElement) {
        return;
      }
      parentElement.scrollIntoView({behavior: 'smooth', block: 'center'});
    }
  });
};
/**
 * Create schema from array of objects
 * @param methods
 * @param values
 * @returns {*}
 */
export const flattenObject = (methods: mixed, values: ?{} = {}): ValidatorsMap<any> | null => {
  const stack = {};
  if (!values) {
    return null;
  }

  Object.keys(values).forEach(key => (stack[key] = methods));
  return stack;
};

export const customFlattenObject = (
  methods: {[string]: Array<Object>},
  values: ?{} = {}
): ValidatorsMap<any> | null => {
  const stack = {};
  if (!values) {
    return null;
  }

  Object.keys(values).map(key => (stack[key] = methods[key]));
  return stack;
};

/**
 * Create nested object
 * @param object
 * @param values
 * @returns {{}}
 */
export const nestedObject = (object: {}, values: {}[] = []): {} => {
  const stack = {};

  values.forEach((value, index) => {
    Object.keys(object).map(key => {
      if (!value[key] || !value[key].length) {
        return;
      }

      stack[index] = {
        ...stack[index],
        [key]: flattenObject(object[key], value[key])
      };
    });
  });

  return stack;
};

export const replaceBadSymbolsForDomain = (domain: string) =>
  domain
    .toLowerCase()
    .replace(/\W/g, '-') // replace all symbols to "-"
    .replace(/[^a-z\d\-\s]/gi, '') // delete trash
    .replace(/[\s-]+/gi, '-') // delete duplicate and replace space to "-"
    .replace(/^[^a-z\d]+/i, '') // delete trash on start
    .replace(/[^a-z\d]+$/i, '') // delete trash on end
    .substr(0, 62);

/**
 * Decorator to convert single-value Field validator to FieldArray validator, that
 * returns global errors
 */
export const fieldArrayValidator = (validator: Validator): Validator => {
  const wrappedValidator: Validator = (...args) => {
    const error = validator(...args);
    return error ? {_error: error} : null;
  };
  return wrappedValidator;
};
